import { encodePeriodValue } from '@components'

import { ItemsPerPage, useItemsPerPage } from '@billy/design-system'
import { createContext, ReactElement, ReactNode, useCallback, useContext, useMemo } from 'react'

import { TransactionOriginatorType } from '@features/transactions/enums/transactionOriginatorType'
import { TransactionOriginatorTypeGroup } from '@features/transactions/enums/transactionOriginatorTypeGroup'
import { TransactionSortProperty } from '@features/transactions/enums/transactionSortProperty'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { useGlobalFiscalYear } from '../../../contexts/globalFiscalYearContext'
import { SortDirection } from '../../../enums/sortDirection'
import { useQueryParams } from '../../../hooks/useQueryParams'
import { getPeriodValueFromFiscalYear } from '../../../utils/getPeriodValueFromFiscalYear'
import { defaultTransactionsQuery } from '../constants/defaultTransactionsQuery'

export interface TransactionsListFilters {
  journalCode?: string[]
  originatorType?: TransactionOriginatorType[]
  originatorTypeGroup?: TransactionOriginatorTypeGroup[]
  page?: number
  pageSize?: ItemsPerPage
  period?: string
  searchQuery?: string
  sortDirection?: SortDirection
  sortProperty?: TransactionSortProperty
  withVoided?: boolean
  withHasVoucher?: boolean
}

interface TransactionsListFiltersContextValue {
  appliedFilters: TransactionsListFilters
  setFilters: (filters: Partial<TransactionsListFilters>) => void
}

const TransactionsListFiltersContext = createContext<TransactionsListFiltersContextValue | undefined>(undefined)

interface TransactionsListFiltersContextProviderProps {
  children: ReactNode
}

export const TransactionsListFiltersContextProvider = ({
  children,
}: TransactionsListFiltersContextProviderProps): ReactElement => {
  const [pageSize] = useItemsPerPage()
  const { organization } = useUserOrganization()
  const { fiscalYear: globalFiscalYear } = useGlobalFiscalYear()
  const [queryParams, setQueryParams] = useQueryParams<TransactionsListFilters>({
    defaultQuery: {
      ...defaultTransactionsQuery,
      pageSize,
      ...(globalFiscalYear && organization?.id
        ? { period: encodePeriodValue(getPeriodValueFromFiscalYear(globalFiscalYear), organization.id) }
        : {}),
    },
  })

  const setFilters = useCallback(
    (params: Partial<TransactionsListFilters>) => {
      setQueryParams(params)
    },
    [setQueryParams],
  )

  const contextValue = useMemo((): TransactionsListFiltersContextValue => {
    const { withVoided, page, pageSize, journalCode, originatorTypeGroup, withHasVoucher } = queryParams
    let parsedOriginatorTypeGroup = originatorTypeGroup
    if (typeof originatorTypeGroup === 'string') {
      // `originatorTypeGroup` comes as a string when it was set as empty array.
      parsedOriginatorTypeGroup = originatorTypeGroup ? [originatorTypeGroup] : []
    }

    let parsedJournalCode = journalCode
    if (typeof journalCode === 'string') {
      // same behavior as above
      parsedJournalCode = journalCode ? [journalCode] : []
    }

    return {
      appliedFilters: {
        ...queryParams,
        // Sometimes `page` comes as a string. It needs to be always `number` type.
        page: Number(page),
        // Sometimes `pageSize` comes as a string. It needs to be always `number` type.
        pageSize: Number(pageSize) as ItemsPerPage,
        // Sometimes `withVoided` comes as a string. It needs to be always `boolean` type.
        withVoided: typeof withVoided === 'string' ? withVoided === 'true' : Boolean(withVoided),
        journalCode: parsedJournalCode,
        originatorTypeGroup: parsedOriginatorTypeGroup,
        withHasVoucher: typeof withHasVoucher === 'string' ? withHasVoucher === 'true' : Boolean(withHasVoucher),
      },
      setFilters,
    }
  }, [queryParams, setFilters])

  return (
    <TransactionsListFiltersContext.Provider value={contextValue}>{children}</TransactionsListFiltersContext.Provider>
  )
}

export const useTransactionsListFilters = (): TransactionsListFiltersContextValue => {
  const context = useContext(TransactionsListFiltersContext)

  if (!context) {
    throw new Error('TransactionsListFiltersContextProvider is missing in the module!')
  }

  return context
}
